/**
 * UmbrellaModel.js — 伞模型生成器
 * 基于程序化几何体创建伞的3D模型
 * 支持八角伞等特殊形状
 */

import * as THREE from 'three';

/**
 * 创建伞的3D模型
 * @param {Object} data - 伞数据
 * @returns {THREE.Group} 伞模型组
 */
export function createUmbrellaModel(data) {
  const group = new THREE.Group();
  const params = data.modelParams || {};

  const ribCount = params.ribCount || 16;
  const canopyRadius = params.canopyRadius || 1.0;
  const canopyHeight = params.canopyHeight || 0.4;
  const handleLength = params.handleLength || 1.3;
  const handleCurve = params.handleCurve ?? 0.15;

  // 1. 伞面 (Canopy)
  let canopy;
  if (data.id === 'guohuo') {
    // 八角伞：八角形伞面 + 真实图片纹理
    canopy = createOctagonalCanopy(canopyRadius, canopyHeight, data);
  } else {
    canopy = createStandardCanopy(canopyRadius, canopyHeight, data);
  }
  group.add(canopy);

  // 2. 伞骨 (Ribs)
  const ribs = createRibs(canopyRadius, canopyHeight, ribCount, data.id === 'guohuo', data.id);
  group.add(ribs);

  // 3. 伞柄 (Handle)
  const handle = createHandle(handleLength, handleCurve, canopyHeight, data);
  group.add(handle);

  // 4. 伞顶 (Top Cap)
  const topCap = createTopCap(canopyHeight, data);
  group.add(topCap);

  return group;
}

/**
 * 八角伞几何常量：顶点方向偏移（与矫正纹理 bajiao_canopy.png 对齐）
 * 纹理中八边形平边朝上，顶点在图像 -67.5°+45°k 方向；
 * 映射到 3D（图像上→+z，图像右→+x）后顶点在 67.5°+45°k。
 */
const OCT_VERTEX_OFFSET = (3 * Math.PI) / 8;

/**
 * 正八边形边界半径：外接圆半径 radius 的正八边形在 θ 方向上的边界距离
 */
function octBoundaryRadius(theta, radius) {
  const apothem = radius * Math.cos(Math.PI / 8);
  const sector = Math.PI / 4;
  let local = (theta - OCT_VERTEX_OFFSET) % sector;
  if (local < 0) local += sector;
  local -= Math.PI / 8;   // 相对边中点方向的角度，∈[-22.5°, 22.5°]
  return apothem / Math.cos(local);
}

/**
 * 伞面剖面高度：t 为距中心的水平距离比例（0 中心 → 1 伞边）
 */
function octProfileY(t, height) {
  return height * (1 - t * t * 0.85);
}

/**
 * 创建八角形伞面（用于八角伞）
 * 真八边形轮廓的极坐标网格 + 平面投影 UV（与俯视矫正纹理一一对应）
 * 上面为照片纹理，内面为素色油纸（避免图案镜像透底）
 */
function createOctagonalCanopy(radius, height, data) {
  const radialSegments = 14;   // 中心到伞边的分段
  const angularCount = 48;     // 角向分段（8 的倍数，顶点恰好落在网格线上）

  const positions = [0, octProfileY(0, height), 0];
  const uvs = [0.5, 0.5];
  const indices = [];

  for (let j = 1; j <= radialSegments; j++) {
    const t = j / radialSegments;
    for (let i = 0; i < angularCount; i++) {
      const theta = (i / angularCount) * Math.PI * 2;
      const d = t * octBoundaryRadius(theta, radius);
      const x = Math.cos(theta) * d;
      const z = Math.sin(theta) * d;
      positions.push(x, octProfileY(t, height), z);
      uvs.push(0.5 + x / (radius * 2), 0.5 + z / (radius * 2));
    }
  }

  const ringStart = (j) => 1 + (j - 1) * angularCount;
  for (let i = 0; i < angularCount; i++) {
    const next = (i + 1) % angularCount;
    // 中心扇面（正面朝上）
    indices.push(0, ringStart(1) + next, ringStart(1) + i);
    for (let j = 1; j < radialSegments; j++) {
      const a = ringStart(j) + i;
      const b = ringStart(j) + next;
      const c = ringStart(j + 1) + i;
      const d = ringStart(j + 1) + next;
      indices.push(a, b, d, a, d, c);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  const group = new THREE.Group();

  // 上面：照片纹理，alphaTest 裁出八边形轮廓
  const topMaterial = createCanopyMaterial(data);
  topMaterial.transparent = false;
  topMaterial.alphaTest = 0.5;
  topMaterial.side = THREE.FrontSide;
  const canopy = new THREE.Mesh(geometry, topMaterial);
  canopy.castShadow = true;
  canopy.receiveShadow = true;
  group.add(canopy);

  // 内面：素色油纸（背面渲染，从伞内看）
  const liningMaterial = new THREE.MeshStandardMaterial({
    color: 0xece2cd,
    roughness: 0.75,
    metalness: 0.0,
    side: THREE.BackSide
  });
  const lining = new THREE.Mesh(geometry, liningMaterial);
  lining.receiveShadow = true;
  group.add(lining);

  // 加载纹理，并为阴影深度材质附带 alpha 裁剪（让地面投影也是八边形）
  loadCanopyTexture(data, topMaterial, (texture) => {
    canopy.customDepthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      map: texture,
      alphaTest: 0.5
    });
  });

  return group;
}

/**
 * 创建标准伞面（LatheGeometry）
 */
function createStandardCanopy(radius, height, data) {
  const points = [];
  const segments = 32;

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const x = t * radius;
    const y = height * Math.pow(1 - t * t, 0.5);
    points.push(new THREE.Vector2(x, y));
  }

  points.unshift(new THREE.Vector2(0, height + 0.02));

  const geometry = new THREE.LatheGeometry(points, 64);
  const material = createCanopyMaterial(data);

  const canopy = new THREE.Mesh(geometry, material);
  canopy.castShadow = true;
  canopy.receiveShadow = true;

  loadCanopyTexture(data, material);

  // 朱红内衬与伞头红结（依《宋代绿油纸伞》图：伞内为朱红色）
  if (data.modelParams?.liningColor) {
    const liningGroup = new THREE.Group();
    liningGroup.add(canopy);

    const liningMat = new THREE.MeshStandardMaterial({
      color: data.modelParams.liningColor,
      roughness: 0.55,
      metalness: 0.0,
      side: THREE.FrontSide  // 该几何体正面朝下（伞内），从下方看为朱红内衬
    });
    const lining = new THREE.Mesh(geometry, liningMat);
    lining.scale.set(0.985, 0.96, 0.985);
    liningGroup.add(lining);

    // 伞头红结：内顶中央的红色绳结
    const knot = new THREE.Mesh(
      new THREE.SphereGeometry(0.045, 12, 8),
      new THREE.MeshStandardMaterial({ color: data.modelParams.liningColor, roughness: 0.5 })
    );
    knot.scale.y = 0.65;
    knot.position.y = height - 0.045;
    liningGroup.add(knot);

    return liningGroup;
  }

  return canopy;
}

/**
 * 创建伞面材质
 */
function createCanopyMaterial(data) {
  const materialConfig = {
    color: 0xffffff,
    roughness: 0.6,
    metalness: 0.05,
    side: THREE.DoubleSide,
    transparent: true,
  };

  // 根据伞的类型调整材质参数
  if (data.id === 'xihu_silk') {
    materialConfig.color = 0xf0e6d8;
    materialConfig.roughness = 0.3;
    materialConfig.metalness = 0.1;
  } else if (data.id === 'pre_qin') {
    materialConfig.color = 0x8b0000;
    materialConfig.roughness = 0.4;
    materialConfig.metalness = 0.2;
  } else if (data.id === 'guohuo') {
    // 八角伞：油纸材质，偏暖黄
    materialConfig.color = 0xf5f0e6;
    materialConfig.roughness = 0.7;
    materialConfig.metalness = 0.0;
  } else if (data.id === 'qing') {
    materialConfig.color = 0xc4956a;
    materialConfig.roughness = 0.5;
  } else if (data.id === 'yuan') {
    materialConfig.color = 0x6b5b4f;
    materialConfig.roughness = 0.7;
  } else if (data.id === 'song') {
    // 宋代绿油纸伞：深松绿漆面、油纸光泽（依《宋代绿油纸伞》图，绿得近黑）
    // 该几何体正面朝下，绿色外皮用背面渲染（伞顶向下看为绿）
    materialConfig.color = 0x2e4234;
    materialConfig.roughness = 0.32;
    materialConfig.metalness = 0.08;
    materialConfig.side = THREE.BackSide;
  } else {
    materialConfig.color = 0xd4a574;
    materialConfig.roughness = 0.6;
  }

  return new THREE.MeshStandardMaterial(materialConfig);
}

/**
 * 尝试加载伞面纹理
 * @param {Function} [onLoad] - 纹理加载成功后的回调
 */
function loadCanopyTexture(data, material, onLoad) {
  const canopySrc = data.images?.canopy;
  if (!canopySrc || canopySrc.trim() === '') return;

  const textureLoader = new THREE.TextureLoader();
  textureLoader.load(
    canopySrc,
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;

      material.map = texture;
      // 如果纹理加载成功，将基础颜色设为白色（让纹理主导）
      material.color.setHex(0xffffff);
      material.needsUpdate = true;

      if (onLoad) onLoad(texture);

      console.log(`[UmbrellaModel] 纹理加载成功: ${canopySrc}`);
    },
    undefined,
    (err) => {
      console.warn('[UmbrellaModel] 伞面纹理加载失败:', err);
    }
  );
}

/**
 * 创建伞骨
 * @param {boolean} isOctagonal - 是否是八角伞（显示八角形轮廓）
 */
function createRibs(radius, height, count, isOctagonal = false, dataId = '') {
  const group = new THREE.Group();

  // 宋代绿油纸伞：伞骨为深色竹骨（依图）
  const ribMaterial = new THREE.MeshStandardMaterial({
    color: dataId === 'song' ? 0x4a3520 : 0x8b6914,
    roughness: 0.5,
    metalness: 0.1
  });

  // 八角伞的特殊处理：32根竹骨（8根主骨对八角 + 每扇形3根次骨），
  // 贴合伞面内壁弧度，直达八边形轮廓，骨尖微露（依馆藏实物俯视照）
  if (isOctagonal) {
    const ribTotal = 32;
    for (let i = 0; i < ribTotal; i++) {
      const theta = OCT_VERTEX_OFFSET + (i / ribTotal) * Math.PI * 2;
      const isMain = i % 4 === 0;   // 每4根有1根落在八角顶点上
      const rEdge = octBoundaryRadius(theta, radius);

      const pts = [];
      const steps = 10;
      for (let j = 0; j <= steps; j++) {
        const t = 0.05 + (j / steps) * 0.95;
        const d = t * rEdge;
        pts.push(new THREE.Vector3(
          Math.cos(theta) * d,
          octProfileY(t, height) - 0.012,   // 贴于伞面之下
          Math.sin(theta) * d
        ));
      }
      // 主伞骨骨尖微露伞边（实物八角顶点处可见短小骨尖），次伞骨齐边收齐
      if (isMain) {
        const tip = pts[steps].clone();
        tip.x += Math.cos(theta) * 0.014;
        tip.z += Math.sin(theta) * 0.014;
        tip.y -= 0.002;
        pts.push(tip);
      }

      const ribCurve = new THREE.CatmullRomCurve3(pts);
      const ribGeo = new THREE.TubeGeometry(ribCurve, 20, isMain ? 0.009 : 0.0055, 5, false);
      const rib = new THREE.Mesh(ribGeo, ribMaterial);
      rib.castShadow = true;
      group.add(rib);
    }

    // 伞顶毂（中心竹木座，伞骨汇聚处）
    const hub = new THREE.Mesh(
      new THREE.CylinderGeometry(0.035, 0.042, 0.1, 12),
      ribMaterial
    );
    hub.position.y = height - 0.055;
    hub.castShadow = true;
    group.add(hub);
  } else if (dataId === 'song') {
    // 宋代绿油纸伞：伞骨贴合伞面内壁弧度（依图：竹骨隐于朱红内衬之上）
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const pts = [];
      for (let j = 0; j <= 12; j++) {
        const t = j / 12;
        const r = t * radius * 0.985;
        const y = height * Math.pow(1 - t * t, 0.5) - 0.012;
        pts.push(new THREE.Vector3(Math.cos(angle) * r, y, Math.sin(angle) * r));
      }
      const ribCurve = new THREE.CatmullRomCurve3(pts);
      const ribGeo = new THREE.TubeGeometry(ribCurve, 16, 0.006, 5, false);
      const rib = new THREE.Mesh(ribGeo, ribMaterial);
      rib.castShadow = true;
      group.add(rib);
    }
  } else {
    // 标准伞骨
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const ribLength = Math.sqrt(radius * radius + height * height);
      const ribGeo = new THREE.CylinderGeometry(0.008, 0.005, ribLength, 4);
      const rib = new THREE.Mesh(ribGeo, ribMaterial);

      const midX = Math.cos(angle) * radius * 0.5;
      const midZ = Math.sin(angle) * radius * 0.5;
      const midY = height * 0.5;

      rib.position.set(midX, midY, midZ);
      rib.rotation.y = -angle;
      rib.rotation.z = Math.atan2(radius, height) - Math.PI / 2;
      rib.castShadow = true;

      group.add(rib);
    }
  }

  // 伞骨圈（环形支撑；八角伞为真实贴面伞骨结构，不加装饰环）
  if (!isOctagonal) {
    const ringRadius = radius * 0.85;
    const ringGeo = new THREE.TorusGeometry(ringRadius, 0.006, 4, count);
    const ring = new THREE.Mesh(ringGeo, ribMaterial);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = height * 0.3;
    group.add(ring);
  }

  return group;
}

/**
 * 创建伞柄
 */
function createHandle(length, curve, canopyHeight, data) {
  const group = new THREE.Group();

  // 根据伞的类型调整伞柄颜色
  let handleColor = 0x5c4033;
  if (data.id === 'guohuo') {
    handleColor = 0x3d2b1f;  // 八角伞：深色老木
  } else if (data.id === 'song') {
    handleColor = 0x9a7b4f;  // 宋代绿油纸伞：竹木原色（依图）
  }

  const handleMaterial = new THREE.MeshStandardMaterial({
    color: handleColor,
    roughness: 0.4,
    metalness: 0.05
  });

  // 主伞柄
  const handleGeo = new THREE.CylinderGeometry(0.025, 0.03, length, 12);
  const handle = new THREE.Mesh(handleGeo, handleMaterial);
  handle.position.y = canopyHeight - length / 2;
  handle.castShadow = true;
  group.add(handle);

  // 伞柄弯头
  if (curve > 0.05) {
    const curveGeo = new THREE.TorusGeometry(curve, 0.025, 8, 16, Math.PI / 2);
    const curveMesh = new THREE.Mesh(curveGeo, handleMaterial);
    curveMesh.position.y = canopyHeight - length;
    curveMesh.rotation.z = Math.PI;
    group.add(curveMesh);

    const endGeo = new THREE.SphereGeometry(0.035, 12, 12);
    const endMesh = new THREE.Mesh(endGeo, handleMaterial);
    endMesh.position.set(-curve, canopyHeight - length - curve, 0);
    group.add(endMesh);
  } else {
    const endGeo = new THREE.SphereGeometry(0.03, 12, 12);
    const endMesh = new THREE.Mesh(endGeo, handleMaterial);
    endMesh.position.y = canopyHeight - length;
    group.add(endMesh);
  }

  return group;
}

/**
 * 创建伞顶
 */
function createTopCap(canopyHeight, data) {
  let capColor = 0xb8a88a;
  let capSize = 0.04;

  if (data.id === 'guohuo') {
    capColor = 0x4a3728;  // 八角伞：深色木帽
    capSize = 0.05;
  }

  // 宋代绿油纸伞：金色葫芦伞顶（依图）
  if (data.id === 'song') {
    const gold = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      roughness: 0.25,
      metalness: 0.65
    });
    const gourd = new THREE.Group();
    const lower = new THREE.Mesh(new THREE.SphereGeometry(0.03, 12, 12), gold);
    lower.position.y = canopyHeight + 0.035;
    lower.castShadow = true;
    const upper = new THREE.Mesh(new THREE.SphereGeometry(0.019, 12, 12), gold);
    upper.position.y = canopyHeight + 0.075;
    const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.009, 0.03, 8), gold);
    tip.position.y = canopyHeight + 0.1;
    gourd.add(lower, upper, tip);
    return gourd;
  }

  const geo = new THREE.SphereGeometry(capSize, 12, 12);
  const material = new THREE.MeshStandardMaterial({
    color: capColor,
    roughness: 0.3,
    metalness: 0.4
  });
  const cap = new THREE.Mesh(geo, material);
  cap.position.y = canopyHeight + 0.02;
  cap.castShadow = true;
  return cap;
}
